import React, { useState } from 'react'
import { useAuthContext } from '../contexts/AuthContext'
import { Button } from './ui/Button'
import { 
  UserIcon, 
  UserGroupIcon, 
  ShieldCheckIcon,
  ArrowPathIcon 
} from '@heroicons/react/24/outline'

export const RoleSwitcher: React.FC = () => {
  const { 
    currentUser, 
    currentRole, 
    hasRole, 
    switchRole, 
    resetRole, 
    isRoleSwitched 
  } = useAuthContext()
  
  const [isOpen, setIsOpen] = useState(false)

  // Only show for admin users in development
  if (!hasRole('admin') || import.meta.env.PROD) {
    return null
  }

  const roles = [
    { id: 'employee', name: 'Employee', icon: UserIcon, color: 'text-blue-600' },
    { id: 'manager', name: 'Manager', icon: UserGroupIcon, color: 'text-green-600' },
    { id: 'admin', name: 'Admin', icon: ShieldCheckIcon, color: 'text-purple-600' },
  ] as const

  const handleRoleSwitch = (role: 'employee' | 'manager' | 'admin') => {
    if (role === currentRole) {
      resetRole()
    } else {
      switchRole(role)
    }
    setIsOpen(false)
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div className="relative">
        {/* Main button */}
        <Button
          onClick={() => setIsOpen(!isOpen)}
          variant="outline"
          size="sm"
          className={`
            shadow-lg border-2 transition-all duration-200
            ${isRoleSwitched 
              ? 'border-warning-500 bg-warning-50 dark:bg-warning-900/20 text-warning-700 dark:text-warning-300 hover:bg-warning-100 dark:hover:bg-warning-900/30' 
              : 'border-neutral-300 dark:border-navy-600/40 bg-white dark:bg-navy-800/70 hover:bg-neutral-50 dark:hover:bg-navy-700/50'
            }
          `}
        >
          <div className="flex items-center space-x-2">
            {isRoleSwitched ? (
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
            ) : (
              <ShieldCheckIcon className="h-4 w-4" />
            )}
            <span className="text-xs font-medium">
              {isRoleSwitched ? 'DEV MODE' : 'Admin'}
            </span>
            {isRoleSwitched && (
              <span className="text-xs bg-warning-200 text-warning-800 px-1 rounded">
                {currentRole}
              </span>
            )}
          </div>
        </Button>

        {/* Dropdown menu */}
        {isOpen && (
          <div className="absolute bottom-full right-0 mb-2 w-48 bg-white dark:bg-navy-800/70 rounded-lg shadow-xl border border-neutral-200 dark:border-navy-700/50 py-2">
            <div className="px-3 py-2 border-b border-neutral-200 dark:border-navy-700/50">
              <p className="text-xs font-medium text-neutral-700 dark:text-neutral-300">Switch Role (Dev Only)</p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">Current: {currentRole}</p>
            </div>
            
            {roles.map((role) => {
              const IconComponent = role.icon
              const isActive = currentRole === role.id
              
              return (
                <button
                  key={role.id}
                  onClick={() => handleRoleSwitch(role.id)}
                  className={`
                    w-full flex items-center space-x-3 px-3 py-2 text-sm transition-colors
                    ${isActive 
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300' 
                      : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-navy-700/50'
                    }
                  `}
                >
                  <IconComponent className={`h-4 w-4 ${role.color}`} />
                  <span className="flex-1 text-left">{role.name}</span>
                  {isActive && (
                    <span className="text-xs bg-primary-100 text-primary-800 px-1 rounded">
                      Active
                    </span>
                  )}
                </button>
              )
            })}
            
            {isRoleSwitched && (
              <div className="border-t border-neutral-200 dark:border-navy-700/50 pt-2">
                <button
                  onClick={() => {
                    resetRole()
                    setIsOpen(false)
                  }}
                  className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-navy-700/50"
                >
                  <ArrowPathIcon className="h-4 w-4 text-neutral-500" />
                  <span>Reset to Actual Role</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Backdrop to close dropdown */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  )
}
